import { Button } from "@/components/ui/button";
import { ArrowRight, Calendar, Briefcase } from "lucide-react";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import heroImage from "@/assets/hero-commerce.jpg";
import { trackCTAClick, trackVideoPlay } from "@/lib/analytics";
import DemoBookingDialog from "./DemoBookingDialog";

const Hero = () => {
  const [hasOpenJobs, setHasOpenJobs] = useState(false);
  const [isDemoDialogOpen, setIsDemoDialogOpen] = useState(false);

  useEffect(() => {
    checkForOpenJobs();
  }, []);

  const checkForOpenJobs = async () => {
    try {
      const { data, error } = await supabase
        .from('job_postings')
        .select('id')
        .eq('published', true)
        .limit(1);

      if (!error && data && data.length > 0) {
        setHasOpenJobs(true);
      }
    } catch (error) {
      console.error('Error checking for jobs:', error);
    }
  };

  return (
    <section className="relative min-h-screen flex items-center gradient-hero overflow-hidden" aria-label="Hero section">
      {/* Background Image Overlay */}
      <img 
        src={heroImage}
        alt="Commerce operations background showing modern retail technology and inventory management systems"
        className="absolute inset-0 w-full h-full object-cover opacity-20"
        fetchPriority="high"
        width={1920}
        height={1080}
      />
      
      {/* Content */}
      <div className="container mx-auto px-4 relative z-10">
        <article className="max-w-4xl mx-auto text-center">
          <div className="bg-background/60 backdrop-blur-md rounded-2xl md:rounded-3xl p-6 md:p-12 shadow-2xl">
            <div className="inline-block mb-6">
              <span className="px-4 py-2 rounded-full bg-primary/20 backdrop-blur-sm border border-primary/30 text-sm font-medium text-orange-600 dark:text-orange-400" role="text">
                Commerce Operations Platform
              </span>
            </div>
            
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold mb-6 leading-tight drop-shadow-2xl">
              Orchestrate Commerce at{" "}
              <span className="text-gradient-hero">Enterprise Scale</span>
            </h1>
            
            <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-foreground mb-8 max-w-3xl mx-auto drop-shadow-xl">
              Your Commerce Operations Platform. One hub for products, inventory, and orders. 
              Built for modern retailers who move fast.
            </p>
          
          <nav className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8" aria-label="Call to action navigation">
            <Button variant="hero" size="xl" asChild>
              <Link 
                to="/free-trial" 
                aria-label="Start your free trial with Ommni commerce operations platform"
                onClick={() => trackCTAClick('Free Trial', 'hero')}
              >
                Free Trial <ArrowRight className="ml-2 h-5 w-5" aria-hidden="true" />
              </Link>
            </Button>
            <Button 
              variant="outline" 
              size="xl"
              onClick={() => {
                setIsDemoDialogOpen(true);
                trackCTAClick('Book a Demo', 'hero');
              }}
              aria-label="Book a demo with Ommni to see the platform in action"
            >
              <Calendar className="mr-2 h-5 w-5" aria-hidden="true" /> Book a Demo
            </Button>
          </nav>
          
          <DemoBookingDialog 
            open={isDemoDialogOpen} 
            onOpenChange={setIsDemoDialogOpen} 
          />
          
          {hasOpenJobs && (
            <Link to="/careers" className="inline-block group" aria-label="View open career positions at Ommni">
              <div className="px-4 py-2 rounded-full bg-gradient-to-r from-green-500/20 to-emerald-500/20 backdrop-blur-sm border border-green-500/30 text-sm font-medium text-green-700 dark:text-green-300 hover:scale-105 transition-transform inline-flex items-center gap-2">
                <Briefcase className="w-4 h-4" aria-hidden="true" />
                <span>We're Hiring! View Open Career Positions</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
              </div>
            </Link>
          )}
          </div>
        </article>
      </div>
      
      {/* Gradient Orbs */}
      <div className="absolute top-1/4 right-0 md:right-1/4 w-48 h-48 md:w-96 md:h-96 bg-primary/20 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 left-0 md:left-1/4 w-48 h-48 md:w-96 md:h-96 bg-accent/20 rounded-full blur-3xl" />
    </section>
  );
};

export default Hero;
